import { ToolId, WorkspaceId } from '../layout/workspace-config';
import { Permission, type PermissionValue } from '../auth/types';
import { ProductionPhase, DashboardWidgetId } from './types';

/**
 * Widget visibility configuration
 */
export interface WidgetVisibility {
    /** Which production phases this widget should appear in */
    phases: ProductionPhase[];
    /** Which workspaces this widget should appear in */
    workspaces: WorkspaceId[];
}

/**
 * Widget configuration
 */
export interface WidgetConfig {
    id: DashboardWidgetId;
    label: string;
    visibility: WidgetVisibility;
    /** If set, the widget is visible only when the user has at least one listed permission. */
    requiredPermissions?: PermissionValue[];
    /** If set, the widget is visible only when at least one linked tool is accessible. */
    requiredTools?: ToolId[];
    /** Grid layout hint (1-4, where 4 is full width) */
    span?: 1 | 2 | 3 | 4;
}

/**
 * Widget registry - defines all available widgets and their visibility rules
 */
export const WIDGET_REGISTRY: WidgetConfig[] = [
    // Cast Member Widgets
    {
        id: 'my-call',
        label: 'My Call',
        visibility: {
            phases: ['pre-production', 'rehearsal', 'tech', 'performance'],
            workspaces: ['cast-member'],
        },
        span: 4, // Hero widget
    },
    {
        id: 'line-study',
        label: 'Line Study',
        visibility: {
            phases: ['rehearsal', 'tech', 'performance'],
            workspaces: ['cast-member'],
        },
        requiredTools: ['rehearsal-script'],
        span: 2,
    },
    {
        id: 'my-props-checklist',
        label: 'My Props Checklist',
        visibility: {
            phases: ['tech', 'performance'],
            workspaces: ['cast-member'],
        },
        span: 2,
    },
    {
        id: 'announcements',
        label: 'Announcements',
        visibility: {
            phases: ['pre-production', 'rehearsal', 'tech', 'performance'],
            workspaces: ['cast-member', 'stage-management', 'director', 'costumes', 'props', 'sound', 'production-manager'],
        },
        span: 2,
    },

    // Stage Manager Widgets
    {
        id: 'daily-schedule',
        label: 'Daily Schedule',
        visibility: {
            phases: ['pre-production', 'rehearsal', 'tech', 'performance'],
            workspaces: ['stage-management', 'director', 'production-manager'],
        },
        span: 4, // Hero widget
    },
    {
        id: 'action-items',
        label: 'Action Items',
        visibility: {
            phases: ['rehearsal', 'tech', 'performance'],
            workspaces: ['stage-management', 'production-manager'],
        },
        span: 2,
    },
    {
        id: 'quick-actions',
        label: 'Quick Actions',
        visibility: {
            phases: ['rehearsal', 'tech', 'performance'],
            workspaces: ['stage-management'],
        },
        span: 2,
    },

    // Director Widgets
    {
        id: 'rehearsal-progress',
        label: 'Rehearsal Progress',
        visibility: {
            phases: ['rehearsal', 'tech'],
            workspaces: ['director', 'stage-management'],
        },
        span: 3,
    },

    // Attendance (Stage Manager)
    {
        id: 'attendance',
        label: "Today's Attendance",
        visibility: {
            phases: ['rehearsal', 'tech', 'performance'],
            workspaces: ['stage-management'],
        },
        span: 2,
    },

    // Production Feed (Universal)
    {
        id: 'production-feed',
        label: 'Production Feed',
        visibility: {
            phases: ['pre-production', 'rehearsal', 'tech', 'performance'],
            workspaces: ['stage-management', 'director', 'costumes', 'props', 'sound', 'production-manager', 'cast-member'],
        },
        span: 2,
    },

    // Activity Summary (SM, Director, PM)
    {
        id: 'activity-summary',
        label: 'Recent Activity',
        visibility: {
            phases: ['pre-production', 'rehearsal', 'tech', 'performance'],
            workspaces: ['stage-management', 'director', 'production-manager'],
        },
        requiredPermissions: [Permission.VIEW_ALL],
        span: 2,
    },
];

/**
 * Get visible widgets for a specific workspace and phase
 */
export function getVisibleWidgets(
    workspace: WorkspaceId | null,
    phase: ProductionPhase,
    userPermissions: PermissionValue[] = [],
    accessibleToolIds?: Set<ToolId>
): WidgetConfig[] {
    if (!workspace) return [];

    return WIDGET_REGISTRY.filter(widget => 
        widget.visibility.workspaces.includes(workspace) &&
        widget.visibility.phases.includes(phase) &&
        (
            !widget.requiredPermissions ||
            widget.requiredPermissions.some(permission => userPermissions.includes(permission))
        ) &&
        (
            !widget.requiredTools ||
            !accessibleToolIds ||
            widget.requiredTools.some(toolId => accessibleToolIds.has(toolId))
        )
    );
}
